import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useUserAuth } from "../lib/userAuth";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function AdminLogin() {
    const { login, user } = useAuth();
    const { signInWithGoogle } = useUserAuth() || {};
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (user && user !== false) nav("/admin", { replace: true });
    }, [user, nav]);

    async function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            toast.success("Welcome back.");
            nav("/admin", { replace: true });
        } else {
            toast.error(res.error || "Login failed");
        }
    }

    return (
        <div className="grid min-h-screen place-items-center bg-page px-5 py-12" data-testid="admin-login-page">
            <div className="w-full max-w-md">
                <Link
                    to="/"
                    className="mb-8 inline-block text-xs font-bold uppercase tracking-[0.22em] text-ink/55 hover:text-coral"
                    data-testid="admin-login-back"
                >
                    ← Back to site
                </Link>

                <div className="border-2 border-ink/20 bg-white p-8 frame-coral lg:p-10">
                    <div className="mb-6 flex items-center gap-3">
                        <div className="grid h-10 w-10 place-items-center border-2 border-coral bg-coral text-ink">
                            <Lock className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="font-display text-2xl font-black uppercase tracking-tight text-ink">
                                Admin
                            </div>
                            <div className="text-xs uppercase tracking-widest text-ink/55">
                                Simple Digital · Control Room
                            </div>
                        </div>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
                        <label className="block">
                            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60">
                                Email
                            </span>
                            <input
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                type="email"
                                required
                                placeholder="admin@simpledigital.co"
                                className="mt-2 w-full border-2 border-ink/20 bg-transparent px-4 py-3 text-ink placeholder:text-ink/30 focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
                                data-testid="admin-login-email"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60">
                                Password
                            </span>
                            <input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                type="password"
                                required
                                placeholder="••••••••"
                                className="mt-2 w-full border-2 border-ink/20 bg-transparent px-4 py-3 text-ink placeholder:text-ink/30 focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
                                data-testid="admin-login-password"
                            />
                        </label>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full border-2 border-coral bg-coral px-5 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:frame disabled:opacity-60"
                            data-testid="admin-login-submit"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                        </button>
                    </form>

                    <div className="my-6 flex items-center gap-3">
                        <div className="h-px flex-1 bg-white/15" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink/45">
                            or
                        </span>
                        <div className="h-px flex-1 bg-white/15" />
                    </div>

                    <button
                        type="button"
                        onClick={() => signInWithGoogle && signInWithGoogle("/admin")}
                        className="flex w-full items-center justify-center gap-3 border-2 border-ink/25 bg-white px-5 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:frame"
                        data-testid="admin-login-google"
                    >
                        <GoogleGlyph />
                        <span>Continue with Google</span>
                    </button>
                    <p className="mt-3 text-[11px] leading-relaxed text-ink/50">
                        Only allow-listed emails can access the dashboard.
                    </p>
                </div>
            </div>
        </div>
    );
}

function GoogleGlyph() {
    return (
        <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden>
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.5 2.6 30.1.5 24 .5 14.6.5 6.5 5.9 2.7 13.7l7.9 6.1C12.5 13.8 17.8 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.4 5.8c4.3-4 6.7-9.9 6.7-17.3z"/>
            <path fill="#FBBC05" d="M10.6 28.8c-.5-1.5-.8-3-.8-4.8s.3-3.3.8-4.8l-7.9-6.1C1 16.8 0 20.3 0 24s1 7.2 2.7 10.9l7.9-6.1z"/>
            <path fill="#34A853" d="M24 47.5c6.1 0 11.2-2 15-5.5l-7.4-5.8c-2 1.4-4.6 2.3-7.6 2.3-6.2 0-11.5-4.3-13.4-10.1l-7.9 6.1C6.5 42.1 14.6 47.5 24 47.5z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
        </svg>
    );
}
